import {
  Language,
  LiveConversationsService,
  LiveConversationSummary,
  LiveConversationTranscript
} from '@shared/openapi/jaaluu-api';
import { Room, RoomEvent, Track } from 'livekit-client';
import { Ref, ref } from 'vue';
import { error } from '.';

export type LiveConversationConnectionState = 'idle' | 'connecting' | 'connected' | 'error';
export type LiveConversationTranscriptStatus = 'idle' | 'loading' | 'ready' | 'error';

// The jaaluu-agent worker only finishes checking the learner's sentences and saving
// the transcript (see backend/jaaluu_api/src/jaaluu_agent/worker.py) once the room has
// actually disconnected -- so the frontend polls GET .../sessions/{id} for a few
// seconds afterwards rather than expecting it immediately.
const TRANSCRIPT_POLL_INTERVAL_MS = 1500;
const TRANSCRIPT_POLL_MAX_ATTEMPTS = 30;

export default () => {
  const conversations: Ref<LiveConversationSummary[]> = ref([]);
  const conversationsLoaded = ref(false);
  const connectionState: Ref<LiveConversationConnectionState> = ref('idle');
  const connectionError: Ref<string | null> = ref(null);
  const sessionId: Ref<string | null> = ref(null);
  const transcript: Ref<LiveConversationTranscript | null> = ref(null);
  const transcriptStatus: Ref<LiveConversationTranscriptStatus> = ref('idle');
  const { handleDefaultError } = error.funcs;
  let room: Room | undefined;
  let transcriptPollTimeout: ReturnType<typeof setTimeout> | undefined;

  const loadConversations = () => {
    return LiveConversationsService.getLiveConversationsV1LiveConversationsGet()
      .then((res) => {
        if (res.data) {
          conversations.value = res.data;
        }
      })
      .catch(handleDefaultError)
      .finally(() => {
        conversationsLoaded.value = true;
      });
  };

  const getConversation = (id: string) => {
    return conversations.value.find((c) => c.id === id) ?? null;
  };

  const disconnectRoom = () => {
    room?.disconnect();
    room = undefined;
  };

  const stopTranscriptPolling = () => {
    if (transcriptPollTimeout) {
      clearTimeout(transcriptPollTimeout);
      transcriptPollTimeout = undefined;
    }
  };

  const fetchFinishedTranscript = (sid: string, attempt = 0) => {
    stopTranscriptPolling();
    LiveConversationsService.getLiveConversationSessionV1LiveConversationsSessionsSessionIdGet({
      path: { session_id: sid }
    })
      .then((res) => {
        // The learner may have already navigated away/started a new call by the time
        // this resolves -- don't let a stale poll clobber a newer session's state.
        if (sessionId.value !== sid) return;
        if (res.data?.status === 'completed') {
          transcript.value = res.data;
          transcriptStatus.value = 'ready';
          return;
        }
        if (attempt >= TRANSCRIPT_POLL_MAX_ATTEMPTS) {
          transcriptStatus.value = 'error';
          return;
        }
        transcriptPollTimeout = setTimeout(
          () => fetchFinishedTranscript(sid, attempt + 1),
          TRANSCRIPT_POLL_INTERVAL_MS
        );
      })
      .catch(() => {
        if (sessionId.value !== sid) return;
        if (attempt >= TRANSCRIPT_POLL_MAX_ATTEMPTS) {
          transcriptStatus.value = 'error';
          return;
        }
        transcriptPollTimeout = setTimeout(
          () => fetchFinishedTranscript(sid, attempt + 1),
          TRANSCRIPT_POLL_INTERVAL_MS
        );
      });
  };

  const connectRoom = async (livekitUrl: string, token: string) => {
    const newRoom = new Room();
    newRoom.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        document.body.appendChild(track.attach());
      }
    });
    newRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => el.remove());
    });
    newRoom.on(RoomEvent.Disconnected, () => {
      room = undefined;
      connectionState.value = 'idle';
      if (sessionId.value) {
        transcriptStatus.value = 'loading';
        fetchFinishedTranscript(sessionId.value);
      }
    });
    try {
      await newRoom.connect(livekitUrl, token);
      await newRoom.localParticipant.setMicrophoneEnabled(true);
    } catch (e) {
      newRoom.disconnect();
      throw e;
    }
    room = newRoom;
  };

  const startConversation = async (conversationId: string, language: Language) => {
    connectionError.value = null;
    connectionState.value = 'connecting';
    sessionId.value = null;
    transcript.value = null;
    transcriptStatus.value = 'idle';
    stopTranscriptPolling();
    try {
      const response =
        await LiveConversationsService.startLiveConversationV1LiveConversationsConversationIdStartPost(
          {
            path: { conversation_id: conversationId },
            body: { language }
          }
        );
      const {
        session_id: newSessionId,
        livekit_url: livekitUrl,
        livekit_token: token
      } = response.data ?? {};
      if (!livekitUrl || !token) {
        throw new Error('Missing LiveKit connection details in response');
      }
      sessionId.value = newSessionId ?? null;
      await connectRoom(livekitUrl, token);
      connectionState.value = 'connected';
    } catch (e) {
      console.error('Error starting live conversation:', e);
      disconnectRoom();
      connectionState.value = 'error';
      connectionError.value = (e as any)?.response?.data?.detail ?? null;
    }
  };

  // User-initiated hangup -- disconnecting the room triggers the RoomEvent.Disconnected
  // handler above, which starts polling for the finished transcript. sessionId/transcript
  // are deliberately left in place so the review can be shown once it's ready.
  const endConversation = () => {
    disconnectRoom();
    connectionState.value = 'idle';
  };

  // Full reset -- unlike endConversation, also clears the session/transcript, e.g. when
  // navigating away from this conversation entirely.
  const resetLiveConversation = () => {
    disconnectRoom();
    stopTranscriptPolling();
    connectionState.value = 'idle';
    sessionId.value = null;
    transcript.value = null;
    transcriptStatus.value = 'idle';
  };

  return {
    state: {
      conversations,
      conversationsLoaded,
      connectionState,
      connectionError,
      transcript,
      transcriptStatus
    },
    funcs: {
      loadConversations,
      getConversation,
      startConversation,
      endConversation,
      resetLiveConversation
    }
  };
};
